import _ from 'lodash';
import styled from 'styled-components';
import { ButtonGroup, FormFeedback, Label } from 'reactstrap';
import { Field, FieldArray, useFormikContext } from 'formik';
import { nanoid } from 'nanoid';
import { gql } from '@apollo/client';
import { useParams } from 'react-router';
import {
  DropdownButtonField,
  ResourceSelectionFormGroup,
  UnitInput,
} from '../template-components';
import {
  useDataTransforms,
  useQueryWithFarmIdAndMsg,
} from '../../../utils/hooks';
import { useDataContext } from '../../../contexts/DataContext';
import { objectWithFields } from '../../../utils/utils';
import { SelectorFormGroup, SquarePlusButton } from './SelectorElements';
import { optionsAreEqualById } from '../../basics/StandardTypeahead';
import { asCurrencyWithCents } from '../../../utils/formatters';

const FlexInput = styled.div`
  flex-grow: 1;
  width: 50px;
`;

export const operationInputFields = ['id', 'amountPerUnit', 'unitForAmount'];

export const inputFields = ['id', 'name', 'unit', 'unitCost'];

const TemplateInputOptionsQuery = gql`
  query TemplateInputOptions {
    inputResources {
      ${inputFields.join(' ')}
    }
  }
`;

const TeamInputOptionsQuery = gql`
  query TeamInputOptions($id: ID!) {
    team(id: $id) {
      id
      inputResources {
        ${inputFields.join(' ')}
      }
    }
  }
`;

const FarmInputOptionsQuery = gql`
  query FarmInputOptions($farmId: ID!) {
    inputResources {
      ${inputFields.join(' ')}
    }
    farmTeamInputTemplates(farmId: $farmId) {
      ${inputFields.join(' ')}
    }
    rawInputs(farmId: $farmId) {
      ${inputFields.join(' ')}
      resourceId
    }
  }
`;

const filterInputResources = (inputResources, resourceIdMap) =>
  inputResources.filter((input) => !resourceIdMap[input.id]);

const customMenuItemChildren = (option) => (
  <small className="ml-1">
    {asCurrencyWithCents(option.unitCost)}/{option.unit}
  </small>
);

const OperationInputs = ({ className, unitOptions, unitsMap }) => {
  const { isTemplate } = useDataContext();
  const { teamId } = useParams();
  const { data } = useQueryWithFarmIdAndMsg(
    isTemplate
      ? teamId
        ? TeamInputOptionsQuery
        : TemplateInputOptionsQuery
      : FarmInputOptionsQuery,
    {
      includeFarmId: !isTemplate,
      variables: { id: teamId },
      // Get latest data if team or public input resource instantiated on a farm
      fetchPolicy: 'cache-and-network',
    },
  );
  const [optionGroups = []] = useDataTransforms(data, [
    (d) => {
      const resourceIdMap = _.keyBy(d.rawInputs || [], 'resourceId');
      return [
        { header: 'Farm Inputs', options: d.rawInputs || [] },
        {
          header: 'Team Inputs',
          options: filterInputResources(
            d.team?.inputResources || [],
            resourceIdMap,
          ),
        },
        {
          header: 'Team Inputs',
          options: filterInputResources(
            d.farmTeamInputTemplates || [],
            resourceIdMap,
          ),
        },
        {
          header: 'Public Inputs',
          options: filterInputResources(d.inputResources || [], resourceIdMap),
        },
      ];
    },
  ]);
  const { setFieldValue, touched, errors, values } = useFormikContext();
  const inputErrors = errors.operationInputs || [];
  const inputTouched = touched.operationInputs || [];
  const inputs = values.operationInputs || [];

  return (
    <ResourceSelectionFormGroup>
      <Label>Inputs</Label>
      <div className={`d-flex ${className || ''}`}>
        <FieldArray name="operationInputs">
          {({ push, remove }) => (
            <>
              {inputs.map(
                ({ localId, id = localId, input, unitForAmount }, i) => {
                  const inputUnit = unitsMap[unitForAmount];
                  const {
                    transformToUnit = _.identity,
                    transformToDefault = _.identity,
                  } = inputUnit || {};
                  return (
                    <div className="mr-2 flex-shrink-0" key={id}>
                      <div>
                        <ButtonGroup>
                          <SelectorFormGroup
                            id={`input-typeahead-${id}`}
                            clearTextOnFocus
                            customMenuItemChildren={customMenuItemChildren}
                            error={inputErrors[i]?.input?.name}
                            isInvalid={
                              !!(
                                inputTouched[i]?.input && inputErrors[i]?.input
                              )
                            }
                            labelKey="name"
                            maxHeight="200px"
                            maxWidth="300px"
                            onChange={([v]) =>
                              setFieldValue(`operationInputs[${i}].input`, v)
                            }
                            optionGroups={optionGroups}
                            optionsAreEqual={optionsAreEqualById}
                            placeholder={input?.name || 'Input'}
                            removeOption={() => remove(i)}
                            selectedOption={input}
                            shouldShowError={inputErrors[i] && inputTouched[i]}
                          />
                        </ButtonGroup>
                      </div>
                      <Field
                        name={`operationInputs[${i}].amountPerUnit`}
                        format={transformToUnit}
                        parse={transformToDefault}
                      >
                        {({
                          field,
                          meta: { touched: fieldTouched, error, initialValue },
                        }) => (
                          <>
                            <div className="d-flex mt-2">
                              <FlexInput>
                                <UnitInput
                                  {...field}
                                  format={transformToUnit}
                                  parse={transformToDefault}
                                  defaultValue={
                                    initialValue == null
                                      ? ''
                                      : transformToUnit(initialValue)
                                  }
                                  invalid={!!(fieldTouched && error)}
                                  className="w-100"
                                  type="number"
                                  min={0}
                                  step="any"
                                  unit={input?.unit}
                                  maxUnitWidth="1.5rem"
                                />
                              </FlexInput>
                              <UnitInput
                                as={DropdownButtonField}
                                inModal
                                outline
                                className="ml-2 flex-shrink-0"
                                options={unitOptions}
                                name={`operationInputs[${i}].unitForAmount`}
                              />
                            </div>
                            {fieldTouched && error && (
                              <FormFeedback className="d-block">
                                {error}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      </Field>
                    </div>
                  );
                },
              )}
              <SquarePlusButton
                onClick={() =>
                  push({
                    ...objectWithFields(operationInputFields),
                    amountPerUnit: 1,
                    unitForAmount: unitOptions[0].value,
                    localId: nanoid(), // A temporary stable id for React
                  })
                }
              />
            </>
          )}
        </FieldArray>
      </div>
    </ResourceSelectionFormGroup>
  );
};

export default OperationInputs;
